import config from '@src/config'

import type Transport from '.'
import { HttpTransaction } from './transaction'

export type CreateThreadResponse = {
  threadId: string
  agentDefinitionId: string
  agentSessionId: string
}

export type CreateThreadRequest = {
  text?: string
  conversationId?: string
  mode?: 'ontology-interview'
}

export type ChatSessionSummary = {
  id: string
  threadId?: string
  conversationId?: string
  title?: string
  createdAt: string
  lastMessageAt: string
}

export type ListChatSessionsRequest = {
  cursor?: string
  limit?: number
}

export type ListChatSessionsResponse = {
  /** Present when more sessions are available. */
  cursor?: string
  sessions: ChatSessionSummary[]
}

export type ListChatSessionMessagesRequest = {
  sessionId: string
  cursor?: string
  limit?: number
}

export type ListChatSessionMessagesResponse = {
  cursor?: string
  messages: unknown[]
}

export type RenameChatSessionRequest = {
  agentSessionId: string
  title: string
}

export default class CopilotClient {
  constructor(private transport: Transport) {}

  // Create a new copilot thread
  createThread(request: CreateThreadRequest): Promise<CreateThreadResponse> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions`,
        body: request,
      }),
    )
  }

  submitToolApproval(
    sessionId: string,
    toolCallId: string,
    approved: boolean,
  ): Promise<void> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions/${sessionId}/tool-approvals/${toolCallId}`,
        body: { approved },
      }),
    )
  }

  stopChatSession({ sessionId }: { sessionId: string }): Promise<void> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'post',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions/${sessionId}/stop`,
      }),
    )
  }

  renameSession({
    agentSessionId,
    title,
  }: RenameChatSessionRequest): Promise<ChatSessionSummary> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'patch',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions/${agentSessionId}`,
        body: { title },
      }),
    )
  }

  // Delete a copilot chat session
  deleteThread({ agentSessionId }: { agentSessionId: string }): Promise<void> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'delete',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions/${agentSessionId}`,
      }),
    )
  }

  // List all copilot chat sessions for the acting user
  listChatSessions(
    request: ListChatSessionsRequest = {},
  ): Promise<ListChatSessionsResponse> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'get',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions`,
        query: {
          cursor: request.cursor,
          limit: request.limit === undefined ? undefined : `${request.limit}`,
        },
      }),
    )
  }

  // Persisted message history for one chat session, newest first.
  listChatSessionMessages({
    sessionId,
    cursor,
    limit,
  }: ListChatSessionMessagesRequest): Promise<ListChatSessionMessagesResponse> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'get',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions/${sessionId}/messages`,
        query: {
          cursor,
          limit: limit === undefined ? undefined : `${limit}`,
        },
      }),
    )
  }

  getChatSession({ sessionId }: { sessionId: string }): Promise<ChatSessionSummary> {
    return this.transport.queue(
      new HttpTransaction({
        method: 'get',
        url: `${config.COPILOT_SERVICE_URL}chat-sessions/${sessionId}`,
      }),
    )
  }
}
